import { AccountIdentifier, ProviderExtraInfo } from '@finxone-platform/shared/sys-config-types';

/**
 * Anything that exposes a list of account identifiers — `AccountInfoType`,
 * `SavingsAccountInfoType`, `NominatedAccountInfoDetailType` and the various
 * dropdown/listing view models all satisfy this.
 */
export type AccountIdentifierHolder = {
  accountIdentifier?: AccountIdentifier[];
};

/**
 * Provider rails that carry a `currency`. Used to determine which account
 * identifiers serve a given currency on a multi-currency account.
 */
const PROVIDER_RAILS: (keyof ProviderExtraInfo)[] = [
  'sepa',
  'swift',
  'faster-payments',
  'chaps',
  'ach',
  'wire',
];

/**
 * Returns the account identifiers that serve `currency`, matched via each
 * provider rail's `currency`. A multi-currency account exposes several
 * identifiers (e.g. one for SEPA/EUR, one for UK Faster Payments/GBP), so a
 * single field such as `sortCode` may only exist on one of them.
 *
 * If no identifier matches the currency (or no currency is supplied — e.g. a
 * single-currency account) all identifiers are returned, preserving the
 * previous `[0]`-based behaviour for accounts that only have one identifier.
 */
export function getIdentifiersForCurrency(
  account: AccountIdentifierHolder | undefined,
  currency: string | undefined,
): AccountIdentifier[] {
  const identifiers = account?.accountIdentifier ?? [];
  if (!currency) {
    return identifiers;
  }
  const matched = identifiers.filter((identifier) =>
    PROVIDER_RAILS.some(
      (rail) => (identifier?.providerExtraInfo as ProviderExtraInfo)?.[rail]?.currency === currency,
    ),
  );
  return matched.length ? matched : identifiers;
}

/**
 * Flat account-identifier fields used to build template models. Values are
 * resolved field-by-field across the supplied identifiers (first non-empty
 * wins), so each field comes from whichever identifier actually holds it.
 */
export interface ResolvedAccountIdentifierFields {
  sortCode: string;
  accountNumber: string;
  iban: string;
  bic: string;
  achAccountNumber: string;
  achNumber: string;
  abaAccountNumber: string;
  abaNumber: string;
  providerExtraInfo: ProviderExtraInfo | Record<string, never>;
  ledgerNumber: string;
  bsbCode: string;
  ifsc: string;
  clabe: string;
  bankCode: string;
  branchCode: string;
  rtnCanada: string;
}

/**
 * Resolves the flat account-identifier fields for an account, scoped to the
 * given currency. Replaces the previous `accountIdentifier[0]` access which
 * silently dropped fields (e.g. UK sort code / account number) that live on a
 * different identifier for multi-currency accounts.
 *
 * @param account  the selected account detail
 * @param currency the currency to scope identifiers to. For a multi-currency
 *                 account this is the currently-selected currency
 *                 (`currentMultiCurrencyCode`); for a single-currency account
 *                 it is the account's own currency. May be empty/undefined.
 */
export function resolveAccountIdentifierFields(
  account: AccountIdentifierHolder | undefined,
  currency: string | undefined,
): ResolvedAccountIdentifierFields {
  const identifiers = getIdentifiersForCurrency(account, currency);

  const firstNonEmpty = (field: keyof AccountIdentifier): string => {
    for (const identifier of identifiers) {
      const value = identifier?.[field];
      if (typeof value === 'string' && value !== '') {
        return value;
      }
    }
    return '';
  };

  const firstRailValue = (
    rail: keyof ProviderExtraInfo,
    getter: (info: NonNullable<ProviderExtraInfo[keyof ProviderExtraInfo]>) => string | undefined | null,
  ): string => {
    for (const identifier of identifiers) {
      const info = (identifier?.providerExtraInfo as ProviderExtraInfo)?.[rail];
      const value = info ? getter(info) : undefined;
      if (value) {
        return value;
      }
    }
    return '';
  };

  const providerExtraInfo =
    identifiers.find(
      (identifier) =>
        identifier?.providerExtraInfo && Object.keys(identifier.providerExtraInfo).length > 0,
    )?.providerExtraInfo ?? {};

  return {
    sortCode: firstNonEmpty('sortCode'),
    accountNumber: firstNonEmpty('accountNumber'),
    iban: firstNonEmpty('iban'),
    bic: firstNonEmpty('bic'),
    achAccountNumber: firstRailValue('ach', (info) => (info as ProviderExtraInfo['ach'])?.accountNumber),
    achNumber: firstRailValue('ach', (info) => (info as ProviderExtraInfo['ach'])?.routingCodes?.aba),
    abaAccountNumber: firstRailValue('wire', (info) => (info as ProviderExtraInfo['wire'])?.accountNumber),
    abaNumber: firstRailValue('wire', (info) => (info as ProviderExtraInfo['wire'])?.routingCodes?.aba),
    providerExtraInfo,
    ledgerNumber: firstNonEmpty('ledgerNumber'),
    bsbCode: firstNonEmpty('bsbCode'),
    ifsc: firstNonEmpty('ifsc'),
    clabe: firstNonEmpty('clabe'),
    bankCode: firstNonEmpty('bankCode'),
    branchCode: firstNonEmpty('branchCode'),
    rtnCanada: firstNonEmpty('rtnCanada'),
  };
}
